import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Switch, TextField } from '@mui/material';
import {
  GetManifoldAndValvesSizingProgressResponse,
  GetSystemResponse,
  UnitDefinition,
  ValveSequencingApplication,
  ValveSequencingCycle,
  VolumetricFlowRateUnits,
} from '../../models/Api';
//styles
import styles from './SizingTools.module.scss';
import './switch.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LightTooltip } from './PdnManifoldSizingTool';
import UnitService from '../../services/UnitService';
import { toDecimals } from '../../utils/Utils';
import { GroupUnion } from '../../models/GroupUnion';

type SavedSizingProgress = GetManifoldAndValvesSizingProgressResponse;
type SetStateSavedSizingProgress = React.Dispatch<React.SetStateAction<SavedSizingProgress>>;

function AirFlowCalculator(props: {
  divisionId: number;
  showAirFlowCalculator: boolean;
  closeAirFlowCalculatorModal: () => void;
  system: GetSystemResponse;
  getApplicationsArray: () => ValveSequencingApplication[];
  commonVolumetricFlowRateUnitName: VolumetricFlowRateUnits | undefined;
  sizingProgress: SavedSizingProgress;
  setSizingProgress: SetStateSavedSizingProgress;
  filteredAirflowUnits: UnitDefinition[];
  getAirflowInCommonUnit: (applicationId: number, customAirflow?: number) => number;
  getValveCv: (applicationId: number) => number;
  validateStationsAndMaxAirflow: (enableTouched: boolean) => boolean | undefined;
  segmentGroup: GroupUnion;
}) {
  const {
    showAirFlowCalculator,
    closeAirFlowCalculatorModal,
    system,
    getApplicationsArray,
    commonVolumetricFlowRateUnitName,
    sizingProgress,
    setSizingProgress,
    filteredAirflowUnits,
    getAirflowInCommonUnit,
    getValveCv,
    validateStationsAndMaxAirflow,
    segmentGroup,
  } = props;
  const { t } = useTranslation();
  const [applicationCycles, setApplicationCycles] = useState<ValveSequencingCycle[]>([
    {
      cycleId: 1,
      applications: getApplicationsArray(),
      derated: undefined,
    },
  ]);
  const [showEnterAirflow, setShowEnterAirflow] = useState<boolean>(false);
  const [trackNewCycleId, setTrackNewCycleId] = useState<number>(1);
  const [maxAirFlow, setMaxAirFlow] = useState<boolean>(false);
  const calculateTotalSum = (applications: ValveSequencingApplication[] | undefined | null, derated?: number) => {
    const applicationAirflowValues = applications?.map((item) => (item.enabled ? item.airflow : 0)) as number[];
    const totalValue = applicationAirflowValues.reduce((total, currentValue) => total + currentValue);
    const deratedTotalValue = derated ? totalValue * (derated / 100) : totalValue;
    return deratedTotalValue;
  };

  const calculateTotalSumForCv = (applications: ValveSequencingApplication[] | undefined | null) => {
    const applicationCvValues = applications?.map((item) => (item.enabled ? item.cv : 0)) as number[];
    const totalValue = applicationCvValues.reduce((total, currentValue) => total + currentValue);
    return totalValue;
  };

  const formatTotalSum = (applications: ValveSequencingApplication[] | undefined | null, derated?: number) => {
    const totalSum = calculateTotalSum(applications, derated);
    return toDecimals(totalSum, system.divisionId as number, 2);
  };

  const formatAirflow = (applicationGroupId: number) => {
    const airflow = getAirflowInCommonUnit(applicationGroupId);
    return toDecimals(airflow, system.divisionId as number, 2);
  };

  const updateApplicationsAirflow = (applications: ValveSequencingApplication[] | null | undefined) => {
    applications?.forEach((application) => {
      if (application.enabled) {
        application.airflow = getAirflowInCommonUnit(application.id as number);
      }
    });
    return applications;
  };

  const getMaxAirFlowFromCalculator = () => {
    const netAirflowValues: number[] = [];
    applicationCycles.forEach((cycle) => netAirflowValues.push(calculateTotalSum(cycle.applications, cycle.derated)));
    return Math.max(...netAirflowValues);
  };

  const getMaxCvFromCalculator = () => {
    const cvValues: number[] = [];
    applicationCycles.forEach((cycle) => cvValues.push(calculateTotalSumForCv(cycle.applications)));
    return Math.max(...cvValues);
  };

  useEffect(() => {
    const applicationCyclesCopy = [...applicationCycles];
    applicationCyclesCopy?.forEach((cycle) => {
      cycle.applications = updateApplicationsAirflow(cycle.applications);
    });
    setApplicationCycles(applicationCyclesCopy);
  }, [sizingProgress.savedSizingRequest?.maxAirflowUnit]);

  useEffect(() => {
    if (sizingProgress.savedSizingRequest) {
      if (sizingProgress.savedSizingRequest.maxAirflow) {
        setShowEnterAirflow(!sizingProgress.savedSizingRequest.isFlowCalculated);
      }

      if (sizingProgress.savedSizingRequest.valveSequencingCycles?.length) {
        sizingProgress.savedSizingRequest.valveSequencingCycles.map((cycle) => {
          //validating newly added application groups.
          system.virtualGroups
            ?.filter((virtualGroup) => virtualGroup.intersectedGroupId === segmentGroup.instance.id)
            .map((application) => {
              let currentApplication: ValveSequencingApplication = {};
              currentApplication = cycle?.applications?.find(
                (existingApplication) => existingApplication.id === application.baseGroupId,
              ) as ValveSequencingApplication;
              if (!currentApplication) {
                currentApplication = {};
                currentApplication.enabled = false;
                currentApplication.id = application.baseGroupId;
                currentApplication.name = system.groups?.find((group) => group.id === application.baseGroupId)?.name;
                currentApplication.airflow = getAirflowInCommonUnit(application.baseGroupId);
                currentApplication.cv = getValveCv(application.baseGroupId);
                cycle.applications?.push(currentApplication);
              } else {
                currentApplication.airflow = getAirflowInCommonUnit(application.baseGroupId);
                currentApplication.cv = getValveCv(application.baseGroupId);
              }
            });
          //validating the deleted application groups
          cycle.applications?.map((application) => {
            const currentApplication = system.virtualGroups
              ?.filter((virtualGroup) => virtualGroup.intersectedGroupId === segmentGroup.instance.id)
              .find((virtualGroup) => virtualGroup.baseGroupId === application.id);
            if (!currentApplication) {
              cycle.applications = cycle.applications?.filter((cycleApp) => cycleApp.id !== application.id);
            }
          });
        });
        setApplicationCycles(sizingProgress.savedSizingRequest.valveSequencingCycles);
        setTrackNewCycleId(sizingProgress.savedSizingRequest.valveSequencingCycles?.length);
        setMaxAirFlow(true);
      }
    }
  }, [sizingProgress]);

  useEffect(() => {
    const sizingProgressUpdated = { ...sizingProgress };
    if (sizingProgressUpdated.savedSizingRequest) {
      sizingProgressUpdated.savedSizingRequest.maxAirflow = getMaxAirFlowFromCalculator();
      sizingProgressUpdated.savedSizingRequest.maxCv = getMaxCvFromCalculator();
    }
    setSizingProgress(sizingProgressUpdated);
  }, [maxAirFlow]);

  const validateDeratedValue = (value: number) => {
    if (value < 0) return 0;
    if (value > 100) return 100;
    return value;
  };

  return (
    <Modal
      open={showAirFlowCalculator}
      onClose={() => {
        closeAirFlowCalculatorModal();
        validateStationsAndMaxAirflow(true);
      }}
    >
      <div className={styles['air-sequence-calculator-modal']}>
        <div className={styles['header-container']}>
          <h5> {t('MachineCycles')}</h5>
          <p>{t('ModelTheMaximumAirflow...')}</p>
        </div>
        {!showEnterAirflow && (
          <div className={styles['calculator-container']}>
            <div className={styles['calculator-header-container']}>
              <h5>{t('CalculateFlow')}</h5>
              {applicationCycles.length < 31 && (
                <Button
                  className={styles['add-cycle-button']}
                  onClick={() => {
                    const applicationsArray: ValveSequencingApplication[] = [];
                    system.virtualGroups
                      ?.filter((virtualGroup) => virtualGroup.intersectedGroupId === segmentGroup.instance.id)
                      .map((virtualGroup) => {
                        const groupObj = system.groups?.find((group) => group.id === virtualGroup.baseGroupId);
                        applicationsArray.push({
                          id: groupObj?.id,
                          name: groupObj?.name,
                          airflow: getAirflowInCommonUnit(groupObj?.id as number),
                          enabled: false,
                          cv: getValveCv(groupObj?.id as number),
                        });
                      });
                    setApplicationCycles([
                      ...applicationCycles,
                      {
                        cycleId: trackNewCycleId + 1,
                        applications: applicationsArray,
                        derated: undefined,
                      },
                    ]);
                    setTrackNewCycleId(trackNewCycleId + 1);
                  }}
                >
                  {t('AddNewCycle')}
                </Button>
              )}
              {applicationCycles.length >= 32 && (
                <span>
                  <LightTooltip title={`${t('TheMaximumNumberOfCycle...')}`} arrow placement="left">
                    <span className={styles['question-sign-icon']}>
                      <img src="images/question_sign.png" />
                    </span>
                  </LightTooltip>
                </span>
              )}
            </div>
            <div className={styles['calculator-table']}>
              <table className={styles['applications-table']}>
                <thead>
                  <tr>
                    <th>{t('Applications')}</th>
                  </tr>
                </thead>
                <tbody>
                  {system.virtualGroups
                    ?.filter((virtualGroup) => virtualGroup.intersectedGroupId === segmentGroup.instance.id)
                    .map((virtualGroup) => {
                      const groupName = system.groups?.find((group) => group.id === virtualGroup.baseGroupId)?.name;
                      return (
                        <tr key={virtualGroup.baseGroupId}>
                          <td>{groupName}</td>
                        </tr>
                      );
                    })}
                  <tr className={styles['calculate-area']}>
                    <td>{t('Totals')}</td>
                  </tr>
                  <tr className={styles['calculate-area']}>
                    <td>{t('Derated')}</td>
                  </tr>
                  <tr className={styles['calculate-area']}>
                    <td>{t('Net')}</td>
                  </tr>
                </tbody>
              </table>

              {applicationCycles &&
                applicationCycles.map((cycle: ValveSequencingCycle) => {
                  return (
                    <table key={cycle.cycleId} className={styles['cycles-table']}>
                      <thead>
                        <tr>
                          <th>
                            <div className={styles['cycles-header-div']}>
                              <span>{`${t('Cycle')} ${cycle.cycleId}`}</span>
                              <div>
                                <FontAwesomeIcon
                                  icon="xmark"
                                  onClick={() => {
                                    const filteredCycles = applicationCycles.filter(
                                      (existingCycleObj: ValveSequencingCycle) => existingCycleObj.cycleId !== cycle.cycleId,
                                    );
                                    setApplicationCycles(filteredCycles);
                                  }}
                                />
                              </div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cycle.applications?.map((application, idx) => {
                          //targetting  the active cell
                          const applicationCyclesCopy = [...applicationCycles];
                          const currentCycle = applicationCyclesCopy.find((existingCycle) => existingCycle.cycleId === cycle.cycleId) as ValveSequencingCycle;
                          const currentApplication = currentCycle?.applications?.find(
                            (existingApplication) => existingApplication.id === application.id,
                          ) as ValveSequencingApplication;
                          return (
                            <tr key={idx}>
                              <td className={styles['switch-container-cell']}>
                                <div>
                                  <span>{t('Off')}</span>
                                  <Switch
                                    className="toggle-switch-sizing"
                                    checked={currentApplication.enabled}
                                    name="calculateFlow"
                                    onChange={() => {
                                      currentApplication.enabled = !currentApplication.enabled;
                                      if (currentApplication.enabled) {
                                        currentApplication.airflow = getAirflowInCommonUnit(application.id as number);
                                        currentApplication.cv = getValveCv(application.id as number);
                                      } else {
                                        currentApplication.airflow = 0;
                                      }
                                      setApplicationCycles(applicationCyclesCopy);
                                    }}
                                  />
                                  <span>{t('On')}</span>
                                  {currentApplication.enabled && (
                                    <div className={styles['airflow-value']}>
                                      {formatAirflow(application.id as number)}
                                      <span>{UnitService.getLabelForUnitName(commonVolumetricFlowRateUnitName, filteredAirflowUnits)}</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        <tr className={styles['calculate-area-total']}>
                          <td>
                            <span>{cycle.applications && formatTotalSum(cycle.applications)}</span>
                            <span>{UnitService.getLabelForUnitName(commonVolumetricFlowRateUnitName, filteredAirflowUnits)}</span>
                          </td>
                        </tr>
                        <tr className={styles['calculate-area-derated']}>
                          <td>
                            <TextField
                              type="number"
                              className={styles['derated-input-field']}
                              defaultValue={cycle.derated ?? 100}
                              InputProps={{
                                inputProps: {
                                  style: { textAlign: 'right' },
                                  min: 0,
                                  max: 100,
                                },
                              }}
                              onChange={(e) => {
                                const value = validateDeratedValue(Number(e.target.value));
                                e.target.value = value.toString() === e.target.value ? e.target.value : value.toString();
                                const applicationCyclesCopy = [...applicationCycles];
                                const currentCycle = applicationCyclesCopy.find(
                                  (existingCycle) => existingCycle.cycleId === cycle.cycleId,
                                ) as ValveSequencingCycle;
                                currentCycle.derated = value;
                                setApplicationCycles(applicationCyclesCopy);
                              }}
                            />
                            <div className={styles['percent-symbol']}>%</div>
                          </td>
                        </tr>
                        <tr className={styles['calculate-area-net']}>
                          <td>
                            <span>{formatTotalSum(cycle.applications, cycle.derated)}</span>
                            <span>{UnitService.getLabelForUnitName(commonVolumetricFlowRateUnitName, filteredAirflowUnits)}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })}
            </div>
          </div>
        )}

        <div className={styles['footer-container']}>
          <div
            className={styles['cancel-button']}
            onClick={() => {
              closeAirFlowCalculatorModal();
              validateStationsAndMaxAirflow(true);
            }}
          >
            {t('Cancel')}
          </div>
          <div
            className={styles['ok-button']}
            onClick={() => {
              closeAirFlowCalculatorModal();
              //update application cycles
              const sizingProgressUpdated = { ...sizingProgress };
              const applicationCyclesCopy = [...applicationCycles];
              applicationCyclesCopy.forEach((cycle) => {
                cycle.derated = cycle.derated ?? 100;
                cycle?.applications?.map((application) => {
                  application.airflow = getAirflowInCommonUnit(application.id as number);
                  application.cv = getValveCv(application.id as number);
                  return application;
                });
              });
              if (sizingProgressUpdated.savedSizingRequest) {
                sizingProgressUpdated.savedSizingRequest.maxAirflow = getMaxAirFlowFromCalculator();
                sizingProgressUpdated.savedSizingRequest.maxCv = getMaxCvFromCalculator();
                sizingProgressUpdated.savedSizingRequest.valveSequencingCycles = applicationCyclesCopy;
                sizingProgressUpdated.savedSizingRequest.isFlowCalculated = !showEnterAirflow;
              }
              setSizingProgress(sizingProgressUpdated);
            }}
          >
            {t('OK')}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AirFlowCalculator;
